class RequestThrottler {
  constructor(maxConcurrent = 2) {
    this.maxConcurrent = maxConcurrent;
    this.running = 0;
    this.queue = [];
    this.stats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      retriedRequests: 0,
    };
  }

  async execute(fn, retries = 3, timeout = 15000) {
    this.stats.totalRequests++;

    return new Promise((resolve, reject) => {
      this.queue.push({ fn, resolve, reject, retries, timeout });
      this.process();
    });
  }

  async process() {
    if (this.running >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    this.running++;
    const { fn, resolve, reject, retries, timeout } = this.queue.shift();

    try {
      const result = await this.executeWithRetry(fn, retries, timeout);
      this.stats.successfulRequests++;
      resolve(result);
    } catch (error) {
      this.stats.failedRequests++;
      reject(error);
    } finally {
      this.running--;
      this.process(); // Process next in queue
    }
  }

  async executeWithRetry(fn, retries, timeout) {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        console.log(
          `[RequestThrottler] Attempt ${attempt}/${retries} for class breaks request`,
        );

        // Add timeout to prevent hanging requests
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error(`Request timeout after ${timeout}ms`)),
            timeout,
          ),
        );

        const result = await Promise.race([fn(), timeoutPromise]);
        console.log(
          `[RequestThrottler] Request succeeded on attempt ${attempt}`,
        );
        return result;
      } catch (error) {
        console.warn(
          `[RequestThrottler] Attempt ${attempt} failed:`,
          error.message,
        );

        if (attempt === retries) {
          console.error(`[RequestThrottler] All ${retries} attempts failed`);
          throw error;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt - 1) * 1000;
        console.log(`[RequestThrottler] Retrying in ${delay}ms...`);
        this.stats.retriedRequests++;

        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // Get current queue status
  getStatus() {
    return {
      running: this.running,
      queued: this.queue.length,
      maxConcurrent: this.maxConcurrent,
      stats: { ...this.stats },
    };
  }

  // Clear queue (useful for cleanup)
  clearQueue() {
    this.queue.forEach(({ reject }) => {
      reject(new Error('Request cancelled - queue cleared'));
    });
    this.queue = [];
    console.log('[RequestThrottler] Queue cleared');
  }

  // Update max concurrent requests
  setMaxConcurrent(max) {
    this.maxConcurrent = Math.max(1, max);
    console.log(
      `[RequestThrottler] Max concurrent requests set to ${this.maxConcurrent}`,
    );
  }
}

// Export singleton instance
export const requestThrottler = new RequestThrottler(2); // Start with 2 concurrent requests
